import { useContext, useEffect, useState } from "react";
import Modal from "../../../utils/components/Modal";
import SelectBox from "../../../utils/components/SelectBox";
import Textarea from "../../../utils/components/Textarea";
import API from "../../../utils/API";
import toastr from "toastr";
import { RequestContext } from "../../../utils/context/RequestContext";

const TaskDelegateModal = ({
  show,
  hideModal,
  translate,
  users = [],
  companies = [],
  groups = [],
  taskId,
  handleSaveForm,
}) => {
  const [selectedDiscipline, setSelectedDiscipline] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [description, setDescription] = useState("");
  const [disableButtons, setDisableButtons] = useState(false);

  const { setRequestLoading } = useContext(RequestContext);

  useEffect(() => {
    if (!show) {
      setSelectedDiscipline([]);
      setSelectedUsers([]);
      setDescription("");
      setDisableButtons(false);
    }
  }, [show]);

  const handleAction = () => {
    if (selectedDiscipline.length === 0) {
      toastr.error(
        translate("tasks.task_delegate.task_delegate_error_no_user")
      );
      return;
    }

    setDisableButtons(true);
    setRequestLoading(true);

    let payload = {
      action_users: selectedDiscipline.map((user) => user.value),
      info_users: selectedUsers.map((user) => user.value),
      description,
      task_id: taskId,
    };

    if (handleSaveForm) handleSaveForm(payload, "delegate");
    else
      API.postData("/workflow/delegate", payload)
        .then(() => {
          toastr.success(
            translate("tasks.task_delegate.task_delegate_success")
          );
          hideModal();
        })
        .catch((err) => {
          toastr.error(translate("tasks.task_delegate.task_delegate_error"));
          console.log(err);
        })
        .finally(() => {
          setDisableButtons(false);
        });
  };

  const handleAddUsers = (type, option, selectType) => {
    const selected =
      selectType === "discipline" ? selectedDiscipline : selectedUsers;
    const setSelected =
      selectType === "discipline" ? setSelectedDiscipline : setSelectedUsers;

    if (type === "company") {
      const company = companies.find(
        (company) => company.value === option.value
      );

      const companyUsers = company.users.filter(
        (user) =>
          selected.findIndex((selectedUser) => selectedUser.value === user.id) <
          0
      );

      setSelected([
        ...selected,
        ...companyUsers.map((user) => ({ value: user.id, label: user.name })),
      ]);
    } else if (type === "user") {
      setSelected([...selected, option]);
    } else if (type === "group") {
      const group = groups.find((group) => group.value === option.value);

      const groupUsers = group.users.filter(
        (user) =>
          selected.findIndex((selectedUser) => selectedUser.value === user.id) <
          0
      );

      setSelected([
        ...selected,
        ...groupUsers.map((user) => ({ value: user.id, label: user.name })),
      ]);
    }
  };

  return (
    <Modal
      show={show}
      title={translate("tasks.task_delegate.title")}
      modalSize="lg"
      positiveButtonNode={translate("send")}
      positiveButtonHandler={() => handleAction()}
      negativeButtonNode={translate("cancel")}
      setShow={() => hideModal()}
      disableButtons={disableButtons}
    >
      <div className="row mt-4">
        <p className="mb-2 text-uppercase fw-bold">
          {translate("tasks.task_delegate.persons_to_be_delegated")}
        </p>
        <SelectBox
          options={[
            {
              label: translate("users"),
              options: users.map((user) => ({ ...user, type: "user" })),
            },
            {
              label: translate("companies"),
              options: companies
                .filter((company) => {
                  const users = company.users.filter(
                    (user) =>
                      selectedDiscipline.findIndex(
                        (selectedUser) => selectedUser.value === user.id
                      ) < 0
                  );

                  return users.length > 0;
                })
                .map(({ value, label }) => ({
                  value,
                  label,
                  type: "company",
                })),
            },
            {
              label: translate("groups"),
              options: groups
                .filter((group) => {
                  const users = group.users.filter(
                    (user) =>
                      selectedDiscipline.findIndex(
                        (selectedUser) => selectedUser.value === user.id
                      ) < 0
                  );

                  return users.length > 0;
                })
                .map(({ value, label }) => ({
                  value,
                  label,
                  type: "group",
                })),
            },
          ]}
          isMulti={true}
          closeMenuOnSelect={false}
          value={selectedDiscipline}
          onChange={(selectedDiscipline, action) => {
            if (`option` in action) {
              handleAddUsers(action.option.type, action.option, "discipline");
            } else setSelectedDiscipline(selectedDiscipline);
          }}
          placeholder={translate(
            "tasks.task_delegate.placeholders.select-persons"
          )}
        />
      </div>
      <div className="row my-5">
        <p className="mb-2 text-uppercase fw-bold">
          {translate("tasks.task_delegate.persons_to_be_informed")}
        </p>
        <SelectBox
          options={[
            {
              label: translate("users"),
              options: users.map((user) => ({ ...user, type: "user" })),
            },
            {
              label: translate("companies"),
              options: companies
                .filter((company) => {
                  const users = company.users.filter(
                    (user) =>
                      selectedUsers.findIndex(
                        (selectedUser) => selectedUser.value === user.id
                      ) < 0
                  );

                  return users.length > 0;
                })
                .map(({ value, label }) => ({
                  value,
                  label,
                  type: "company",
                })),
            },
            {
              label: translate("groups"),
              options: groups
                .filter((group) => {
                  const users = group.users.filter(
                    (user) =>
                      selectedUsers.findIndex(
                        (selectedUser) => selectedUser.value === user.id
                      ) < 0
                  );

                  return users.length > 0;
                })
                .map(({ value, label }) => ({
                  value,
                  label,
                  type: "group",
                })),
            },
          ]}
          isMulti={true}
          closeMenuOnSelect={false}
          value={selectedUsers}
          onChange={(selectedUsers, action) => {
            if (`option` in action) {
              handleAddUsers(action.option.type, action.option, "info");
            } else setSelectedUsers(selectedUsers);
          }}
          placeholder={translate(
            "tasks.task_delegate.placeholders.select-persons"
          )}
        />
      </div>
      <div className="row mb-4">
        <p className="mb-2 text-uppercase fw-bold">
          {translate("tasks.task_delegate.description")}
        </p>
        <Textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder={translate("tasks.task_delegate.description_placeholder")}
        />
      </div>
    </Modal>
  );
};

export default TaskDelegateModal;
